"use client";

import { useState, type ReactNode } from "react";
import { useLiveSession } from "@/contexts/live-session-context";
import type { LocalAudioLatency } from "@/lib/live/live-latency";
import type { LiveParticipant } from "@/lib/live/live-transport";
import type { LiveParticipantLine, LiveParticipantsReply } from "@/lib/live/session-control";
import { heardMs, type LivePeerInfo } from "@/lib/live/session-state";
import { useLiveParticipants } from "@/lib/live/use-live-participants";
import { LiveDiagnostics } from "./live-diagnostics";
import { RecitalInvite } from "./recital-controls";
import { RecitalAudience } from "./recital-audience";
import { LiveIcon, ParticipantsIcon } from "../ui/atoms/icons";

/**
 * Largeur de la feuille, reprise de `GeneratePanel`.
 *
 * Le même plafond, et pour la même raison : le panneau est posé en absolu
 * au-dessus du roll, et 380 px fixes recouvrent entièrement un écran de 390.
 * Les 48 px de gouttière sont la cible tactile minimale pour atteindre le
 * voile et refermer.
 *
 * Deux panneaux se partagent maintenant ce logement, et ils doivent mesurer
 * pareil : deux largeurs proches feraient sauter la bordure gauche d'un
 * panneau à l'autre.
 */
const PANEL_WIDTH = "min(380px, calc(100vw - 48px))";

/**
 * Le relevé des participants hors du fournisseur live : rien à demander.
 * Déclaré une fois, pour que le relevé ne se relance pas à chaque rendu.
 */
async function noParticipants(): Promise<LiveParticipantsReply | null> {
  return null;
}

/**
 * La séance live, dépliée dans le bandeau de droite.
 *
 * # Pourquoi elle a quitté le popover
 *
 * Elle a d'abord vécu dans une bulle ancrée au bouton. Ça convenait à trois
 * lignes — « ouverte », le lien, « arrêter » — et plus du tout à ce qu'elle
 * montre aujourd'hui : le régime, une fiche par participant, sept nombres de
 * latence, le transport, les diagnostics. Une bulle qui dépasse la hauteur de
 * l'écran défile sous le pointeur et se referme au moindre clic à côté, ce qui
 * est exactement ce qu'on ne veut pas d'un panneau qu'on garde ouvert pendant
 * qu'on joue et qu'on regarde bouger.
 *
 * Le bandeau, lui, reste. C'est le même logement que la génération d'exercice,
 * et les deux s'excluent — `StudioPage` tient l'exclusion.
 *
 * # Tout se lit dans le contexte
 *
 * Aucune donnée en propriété : le panneau lit `useLiveSession()`, comme
 * `LiveDiagnostics`. Le bouton de la barre n'a donc rien à transporter, et la
 * quinzaine de propriétés qu'il relayait — chacune à déclarer deux fois, à
 * retyper, à ne pas oublier — a disparu avec le popover.
 *
 * # La verbosité est voulue
 *
 * Les sept lignes de latence et les diagnostics ne sont pas un écran de
 * réglages : c'est l'instrument. Tant que l'alignement n'est pas éprouvé sur
 * deux machines, ils restent à l'écran — voir `LatencyReadout` pour ce que
 * chaque nombre permet de localiser.
 *
 * # Un récital se pilote depuis le rouleau
 *
 * Depuis le 2026-09-20, les commandes d'un récital — ouvrir au public,
 * l'enregistrement, la prise, la trame, la caméra, la salle et la sortie — sont
 * des widgets posés sur le rouleau (`recital-widgets.tsx`) : ce panneau les
 * recouvrait au moment même où l'on s'en servait. Il ne garde d'un récital que
 * ce qui se lit, et le reste de ce fichier ne sert plus qu'à deux écrans : le
 * spectateur, et un live sans récital.
 *
 * # Le spectateur n'a que des lectures
 *
 * Dans un récital, celui qui regarde ne pilote rien : ni le régime, ni le
 * lead, ni le transport — le contexte refuse déjà ces trois gestes, et un
 * bouton qui les proposerait ne ferait que dire « le service n'a pas pris ».
 * Il garde ce qui se lit : qui est là, ce que coûte sa machine, les
 * diagnostics, et la porte de sortie. Ce que coûtent les autres, le service
 * ne le rend qu'à qui peut jouer. C'est aussi pourquoi il garde le panneau
 * plutôt qu'un widget : fermé, il ne prend rien de ce qu'il est venu voir.
 *
 * # La liste ne se relève que panneau ouvert
 *
 * Qui est là, et ce que chacun coûte, se demandent au service toutes les
 * trois secondes tant que le panneau est ouvert, et jamais sinon (voir
 * `useLiveParticipants`) : fermé, il reste monté à largeur nulle.
 */
export function LiveSessionPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const live = useLiveSession();
  const spectator = live?.isListener ?? false;
  const recital = live?.session?.recital ?? null;
  // Sans public : un enregistrement, où il n'y a personne à compter ni à inviter.
  const recordingOnly = recital?.recordingOnly === true;
  const inSession = live !== null && live.status !== "off";
  // Un récital se commande depuis le rouleau : ici, il ne reste qu'à lire.
  const onRoll = recital !== null && !spectator;
  const lines = useLiveParticipants(
    live?.fetchParticipants ?? noParticipants,
    live?.room ?? null,
    open && inSession,
  );

  /*
    Le conteneur anime sa largeur, la feuille sa translation — deux mouvements
    pour un seul geste, exactement comme `GeneratePanel`. Le roll récupère la
    place à la fermeture sans qu'on ait à le lui dire.
  */
  return (
    <div
      className="h-full shrink-0 relative z-10 overflow-hidden transition-[width] duration-200 ease-out"
      style={{ width: open ? PANEL_WIDTH : 0 }}
    >
      <div
        className="h-full flex flex-col bg-bg-surface border-l border-border-subtle transition-transform duration-200 ease-out"
        style={{
          width: PANEL_WIDTH,
          transform: open ? "translateX(0)" : "translateX(100%)",
        }}
      >
        <header className="flex items-center justify-between px-4 h-12 shrink-0 border-b border-border-subtle">
          <div className="flex items-center gap-2 min-w-0">
            <LiveIcon size={14} className="text-red-300 shrink-0" />
            <span className="text-sm font-semibold text-text-primary truncate">
              {recital === null ? "Live" : recordingOnly ? "Enregistrement" : "Récital"}
            </span>

            {/* Nous tous, soi compris, dès qu'on n'est plus seul. */}
            {live !== null && !recordingOnly && live.headcount > 1 && (
              <span className="inline-flex items-center gap-1 text-[11px] text-text-muted shrink-0">
                <ParticipantsIcon size={11} />
                <span className="tabular-nums">{live.headcount}</span>
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="w-7 h-7 flex items-center justify-center rounded-md text-text-muted hover:text-text-primary hover:bg-white/[0.05] transition-colors cursor-pointer"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M4 4l6 6M10 4l-6 6" />
            </svg>
          </button>
        </header>

        {live === null ? null : live.error !== null ? (
          <div className="flex-1 min-h-0 px-4 py-4">
            <p className="text-[13px] text-red-300">{live.error}</p>
          </div>
        ) : (
          <>
            <div
              className="flex-1 min-h-0 overflow-y-auto px-4 py-4 flex flex-col gap-4"
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(255,255,255,0.12) transparent",
              }}
            >
              {recital === null && <Presence count={live.headcount - 1} />}

              {/* Un récital n'a qu'un interprète : ni régime ni lead à régler. */}
              {!spectator && recital === null && (
                <Section title="Régime">
                  <JamSwitch on={live.jam} delayMs={live.delayMs} onChange={live.setJam} />

                  {live.sessionState !== null && (
                    <LeadControl
                      lead={live.lead}
                      isLeader={live.isLeader}
                      jam={live.jam}
                      participants={live.participants}
                      onChange={live.setLead}
                    />
                  )}
                </Section>
              )}

              {recital === null ? (
                <Section title="Participants">
                  <LatencyReadout
                    identity={live.identity}
                    lines={lines?.participants ?? null}
                    jam={live.jam}
                    spectator={spectator}
                    localLatency={live.localLatency}
                    lead={live.lead}
                    emitting={live.emitting}
                    delayMs={live.delayMs}
                  />
                </Section>
              ) : (
                /* L'interprète a sa salle sur le rouleau, avec le lien pour la
                   remplir ; il ne reste ici que le spectateur, à qui elle dit
                   simplement qu'il n'est pas seul. */
                spectator && (
                  <RecitalAudience
                    identity={live.identity}
                    lines={lines?.participants ?? null}
                    headcount={live.headcount}
                    paused={live.sessionState?.paused ?? null}
                  />
                )
              )}

              {/* Un live sans récital n'a pas de barre de transport à lui : ce
                  bouton reste sa seule porte vers la grille de temps. Un
                  récital, lui, se lance depuis la barre du studio, et le motif
                  d'un refus s'affiche sur le rouleau. */}
              {!spectator && recital === null && (
                <Section title="Transport">
                  <TransportControl
                    paused={live.sessionState?.paused ?? null}
                    noun="le live"
                    failure={live.startFailure}
                    onChange={live.setTransportPaused}
                  />
                </Section>
              )}

              <DebugDrawer>
                {(expanded) => (
                  <>
                    {recital !== null && (
                      <LatencyReadout
                        identity={live.identity}
                        lines={lines?.participants ?? null}
                        jam={live.jam}
                        spectator={spectator}
                        localLatency={live.localLatency}
                        lead={live.lead}
                        emitting={live.emitting}
                        delayMs={live.delayMs}
                      />
                    )}

                    <LiveDiagnostics active={open && expanded} />
                  </>
                )}
              </DebugDrawer>
            </div>

            {/*
              Le pied du panneau : comment on entre, et comment on sort.

              Rien de tout cela pour l'interprète d'un récital — le lien, le
              code et la sortie sont sur le rouleau (`recital-widgets.tsx`), et
              deux portes pour un même geste, dont l'une derrière un panneau
              fermé, c'est une de trop. Il reste au spectateur, qui n'a que ce
              panneau, et à un live sans récital, qui n'a pas de widgets.
            */}
            {!onRoll && !recordingOnly && (
            <div className="shrink-0 px-4 pb-4 pt-3 border-t border-border-subtle flex flex-col gap-2">
              <RecitalInvite />

              <button
                type="button"
                onClick={() => {
                  onClose();
                  void live.leave();
                }}
                className="w-full h-9 rounded-lg text-[12px] font-semibold text-red-300 bg-red-500/10 hover:bg-red-500/15 transition-colors duration-150 cursor-pointer"
              >
                {/* Un spectateur qui sort n'arrête rien : le récital continue
                    sans lui. Le verbe le dit. */}
                {spectator ? "Quitter le récital" : "Arrêter le live"}
              </button>
            </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Un intertitre et ce qu'il coiffe.
 *
 * Le panneau montrait des choses de nature différente — un réglage de salle,
 * des mesures, une commande, un instrument — et elles se lisaient d'affilée,
 * sans rien pour dire où l'une s'arrête. Le titre n'ajoute pas d'information,
 * il donne des prises. Il n'en reste que trois depuis que le récital se pilote
 * sur le rouleau ; elles se lisent toujours mieux séparées.
 */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-[10px] font-semibold uppercase tracking-[0.08em] text-text-muted/70">
        {title}
      </h3>

      {children}
    </section>
  );
}

/**
 * Les mesures de mise au point, repliées en bas du panneau.
 *
 * Latences, délais, positions et horloges ne disent rien à qui joue ou
 * regarde un récital : ce sont des outils pour nous. Ils restent là, fermés
 * par défaut, le temps d'en avoir fini avec la synchronisation ; on les
 * supprimera ensuite. Les diagnostics ne s'échantillonnent que déplié.
 */
function DebugDrawer({ children }: { children: (expanded: boolean) => ReactNode }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <details
      className="group mt-auto rounded-lg border border-dashed border-border-subtle"
      onToggle={(event) => setExpanded(event.currentTarget.open)}
    >
      <summary className="flex cursor-pointer select-none items-center justify-between px-3 py-2 font-mono text-[10px] uppercase tracking-[0.12em] text-text-muted/80 hover:text-text-muted [&::-webkit-details-marker]:hidden">
        Debug
        <span aria-hidden="true" className="transition-transform duration-150 group-open:rotate-90">
          ›
        </span>
      </summary>

      {expanded && <div className="flex flex-col gap-3 px-3 pb-3">{children(expanded)}</div>}
    </details>
  );
}

/** Qui est là, en une phrase. */
function Presence({ count }: { count: number }) {
  return (
    <p className="text-[12px] text-text-secondary">
      {count === 0
        ? "Personne d'autre pour l'instant. Envoie le lien."
        : `${count} autre${count > 1 ? "s" : ""} en ligne.`}
    </p>
  );
}

/**
 * Qui mène, et le bouton pour prendre ou rendre le lead.
 *
 * # Ce que le bouton décide
 *
 * Un geste sur soi, envoyé au service : prendre le lead éteint le jam pour
 * toute la séance, le rendre le laisse vacant — et le prochain qui annonce le
 * prend au tour d'après, comme quand un leader ferme son onglet. Rien n'est
 * appliqué localement en attendant : l'état revient par la métadonnée, et
 * c'est elle que la ligne reflète, y compris quand c'est quelqu'un d'autre qui
 * vient de le prendre. Le temps de la réponse, le bouton se grise ; si le
 * service refuse, il le dit dessous.
 *
 * Le bouton reste offert en jam : y prendre le lead est la façon d'en sortir
 * en désignant qui joue, et c'est le service qui tient l'exclusion.
 *
 * # Le nom du leader
 *
 * Il vient de la liste des participants, où l'on ne figure pas — d'où « tu ».
 * Un leader qui vient de fermer son onglet n'y a plus de nom mais tient
 * encore le lead quelques secondes, le temps que le service le libère : on
 * montre alors son identité plutôt que « personne », qui serait faux pendant
 * que la salle attend.
 */
function LeadControl({
  lead,
  isLeader,
  jam,
  participants,
  onChange,
}: {
  lead: string;
  isLeader: boolean;
  jam: boolean;
  participants: readonly LiveParticipant[];
  onChange: (take: boolean) => Promise<boolean>;
}) {
  const [pending, setPending] = useState(false);
  const [refused, setRefused] = useState(false);

  const handleClick = async () => {
    setPending(true);
    setRefused(false);

    try {
      const accepted = await onChange(!isLeader);
      setRefused(!accepted);
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2 rounded-lg border border-border-subtle bg-white/[0.02] px-2.5 py-2">
        <span
          className={`text-[12px] ${isLeader ? "text-text-primary" : "text-text-secondary"}`}
        >
          {leadLabel(lead, isLeader, jam, participants)}
        </span>

        <button
          type="button"
          disabled={pending}
          onClick={() => void handleClick()}
          title={
            isLeader
              ? "Laisser le lead vacant : le prochain qui annonce le prend."
              : "Jouer seul·e pour la salle, sans latence. Éteint le mode Jam."
          }
          className="shrink-0 h-7 px-2.5 rounded-md text-[12px] font-medium text-text-primary bg-bg-elevated hover:bg-bg-hover transition-colors duration-150 cursor-pointer disabled:cursor-wait disabled:opacity-60"
        >
          {isLeader ? "Rendre le lead" : "Prendre le lead"}
        </button>
      </div>

      {refused && (
        <p className="px-1 text-[11px] text-red-300">
          {"Le service n'a pas pris le changement. Le motif est dans la console du navigateur."}
        </p>
      )}
    </div>
  );
}

function leadLabel(
  lead: string,
  isLeader: boolean,
  jam: boolean,
  participants: readonly LiveParticipant[],
): string {
  if (jam) {
    return "Tout le monde joue.";
  }

  if (lead === "") {
    return "Personne ne mène.";
  }

  if (isLeader) {
    return "Tu mènes.";
  }

  const name = participants.find((participant) => participant.id === lead)?.name ?? lead;

  return `${name} mène.`;
}

/**
 * La latence, décomposée, telle que le service la tient.
 *
 * Trois nombres, parce que trois leviers différents : le réseau ne se corrige
 * pas depuis l'application, l'ordonnancement local se règle au palier de perf,
 * et ce que le pair ajoute ne se règle que chez lui. Afficher une seule somme
 * cacherait lequel des trois est en cause — et c'est presque toujours le
 * deuxième ou le troisième.
 *
 * # Deux sens, et pas toujours les deux
 *
 * « M'entend » est ce que le pair perçoit du geste qu'on vient de faire, et
 * n'a de sens que si l'on émet : un auditeur n'est entendu de personne, et la
 * ligne se retire plutôt que d'afficher un chiffre sur un son qui n'existe
 * pas. « Je l'entends » est le symétrique — ce qu'on perçoit de son geste —,
 * et n'a de sens que s'il émet lui-même : le leader, ou tout le monde en jam.
 * La formule est la même dans les deux sens, les rôles échangés ; voir
 * `heardMs`.
 *
 * # Tout vient de la route des participants
 *
 * Les fiches se lisent dans les lignes que le service rend (voir
 * `useLiveParticipants`) : ce que chaque participant lui a annoncé et le délai
 * qu'il en a reçu, la nôtre comprise. « Ma liaison » est donc notre jambe
 * **telle que le service la tient** : si elle reste à « — », c'est que notre
 * annonce ne lui parvient pas, et les délais se calculent sans nous. C'est
 * exactement le genre de panne qu'un chiffre local aurait masqué.
 *
 * « Son délai » est le nombre que le pair pose sur son transport, rendu par
 * le service : zéro s'il mène, son trajet depuis le leader s'il écoute, le
 * même que tout le monde en jam. Le voir chez chacun dit d'un coup d'œil
 * quel régime tourne et qui attend combien.
 *
 * Une fiche par participant que le service voit, spectateurs compris, que
 * LiveKit ne montre plus. Celui qui vient d'entrer n'y est pas avant sa
 * première annonce, et celui qui vient de partir y reste le temps que le
 * service l'oublie.
 *
 * # Les noms seuls, sans mesures
 *
 * Le service ne rend les mesures qu'à qui peut jouer. Une ligne sans mesures
 * n'a donc pas de fiche, mais son nom reste dans la liste : c'est tout ce
 * qu'un spectateur voit des autres, et c'est assez pour savoir qui est là.
 *
 * # Deux liaisons, pas une distance
 *
 * Rien ne va d'un participant à un autre : tout passe par le SFU. On montre
 * donc les deux moitiés du trajet séparément — la nôtre et la sienne — parce
 * qu'elles ne se soignent pas au même endroit. Une valeur unique dirait « c'est
 * lent » sans dire à qui s'adresser, ce que ce panneau refuse déjà de faire
 * pour la latence audio.
 */
export function LatencyReadout({
  identity,
  lines,
  jam,
  spectator,
  localLatency,
  lead,
  emitting,
  delayMs,
}: {
  identity: string | null;
  /** Les lignes du dernier relevé du service, `null` tant qu'aucun n'a abouti. */
  lines: readonly LiveParticipantLine[] | null;
  jam: boolean;
  /** Spectateur de récital : il n'a pas de mesures à attendre. */
  spectator: boolean;
  localLatency: LocalAudioLatency;
  lead: string;
  emitting: boolean;
  /** Mon délai, tel que le contexte l'a appliqué. */
  delayMs: number;
}) {
  const own = lines?.find((line) => line.identity === identity)?.measures ?? null;
  const ownLegMs = own?.legMs ?? null;
  const others = (lines ?? []).filter((line) => line.identity !== identity);
  const rows = others.flatMap((line) =>
    line.measures === null ? [] : [{ line, info: line.measures }],
  );
  const names = others.filter((line) => line.measures === null);

  // Ce que je suis, vu comme une ligne du service : ma jambe telle qu'il la
  // tient, mon délai tel que je l'applique, ma sortie telle que ma machine la
  // mesure — plus fraîche que ce que le service a de moi.
  const me: LivePeerInfo = {
    legMs: ownLegMs ?? 0,
    lookAheadMs: localLatency.lookAheadMs,
    outputMs: localLatency.outputMs,
    // Le trajet vient du service : c'est ce qu'il en a retenu, donc ce sur
    // quoi il calcule. Le relire localement montrerait une valeur plus fraîche
    // que celle qui décide, ce qui est exactement ce qu'on ne veut pas d'un
    // panneau de diagnostic.
    transitMs: own?.transitMs ?? 0,
    delayMs,
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Un spectateur n'attend pas de mesures : la liste des noms arrive au
          premier relevé, sans rien à annoncer d'ici là. */}
      {lines === null && !spectator && (
        <p className="text-[12px] text-text-muted">Latence : mesure en cours…</p>
      )}

      {lines !== null && others.length === 0 && (
        <p className="text-[12px] text-text-muted">{"Personne d'autre pour l'instant."}</p>
      )}

      {rows.map(({ line, info }) => {
        const leads = line.identity === lead;
        // Notre jambe inconnue du service compte pour zéro : le trajet est
        // alors sous-estimé, mais seulement le temps d'un tour d'annonce, et
        // la ligne « ma liaison » dit qu'il manque.
        const hearsMe = heardMs(ownLegMs ?? 0, info);
        const heardByMe = heardMs(info.legMs, me);

        return (
          <article
            key={line.identity}
            className="rounded-lg border border-border-subtle bg-white/[0.02] px-2.5 py-2 flex flex-col gap-2"
          >
            <header className="flex items-baseline justify-between gap-2">
              {/* Nommé, et pas seulement « il » : c'est ce qui permet de voir
                  d'un coup d'œil qu'un participant de trop est le fantôme
                  d'un onglet précédent, que le service garde le temps
                  d'oublier ses annonces. */}
              <span className="text-[12px] font-medium text-text-primary truncate">
                {line.name}
              </span>

              {leads && (
                <span className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium text-amber-300/90 bg-amber-500/10">
                  mène
                </span>
              )}
            </header>

            {(emitting || jam || leads) && (
              <div className="flex gap-2">
                {emitting && <Headline label="m'entend" valueMs={hearsMe} />}

                {(jam || leads) && <Headline label="je l'entends" valueMs={heardByMe} />}
              </div>
            )}

            <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5 text-[11px] text-text-muted">
              <Detail label="ma liaison">
                {ownLegMs === null ? "—" : `${Math.round(ownLegMs / 2)} ms`}
              </Detail>
              <Detail label="sa liaison">{`${Math.round(info.legMs / 2)} ms`}</Detail>
              {/* Ce que les notes lui ont vraiment coûté, contre ce que sa
                  liaison prétend. L'écart entre les deux lignes est tout le
                  sujet : une liaison qui annonce 4 ms peut faire payer 190. */}
              <Detail label="son trajet">
                {info.transitMs <= 0 ? "—" : `${Math.round(info.transitMs)} ms`}
              </Detail>
              <Detail label="son délai">{`${Math.round(info.delayMs)} ms`}</Detail>
              <Detail label="son ordonnancement">{`${Math.round(info.lookAheadMs)} ms`}</Detail>
              <Detail label="sa sortie audio">{`${Math.round(info.outputMs)} ms`}</Detail>
            </dl>
          </article>
        );
      })}

      {names.length > 0 && (
        <ul className="flex flex-col gap-0.5 text-[12px] text-text-secondary">
          {names.map((line) => (
            <li key={line.identity} className="truncate">
              {line.name}
            </li>
          ))}
        </ul>
      )}

      <div className="rounded-lg border border-border-subtle bg-white/[0.02] px-2.5 py-2 flex flex-col gap-1.5">
        <span className="text-[12px] font-medium text-text-primary">Ma machine</span>

        <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5 text-[11px] text-text-muted">
          <Detail label="mon trajet">
            {me.transitMs <= 0 ? "—" : `${Math.round(me.transitMs)} ms`}
          </Detail>
          <Detail label="mon délai">{`${Math.round(delayMs)} ms`}</Detail>
          <Detail label="mon ordonnancement">{`${Math.round(localLatency.lookAheadMs)} ms`}</Detail>
          <Detail label="ma sortie audio">{`${Math.round(localLatency.outputMs)} ms`}</Detail>
        </dl>
      </div>
    </div>
  );
}

/**
 * Un des deux nombres qu'on regarde en jouant, en gros et en couleur.
 *
 * Ils étaient sur la même grille que les cinq autres, à la même taille : on
 * les cherchait. Ce sont pourtant les seuls qui disent si c'est jouable — les
 * autres disent seulement à qui s'adresser quand ça ne l'est pas.
 */
function Headline({ label, valueMs }: { label: string; valueMs: number }) {
  return (
    <div className="flex-1 min-w-0 rounded-md bg-black/20 px-2 py-1.5">
      <div className="text-[10px] text-text-muted truncate">{label}</div>
      <div className={`text-[15px] font-semibold tabular-nums ${verdictColor(valueMs)}`}>
        ~{Math.round(valueMs)} ms
      </div>
    </div>
  );
}

/** Une ligne de la grille de détail : l'intitulé, puis le nombre, aligné. */
function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd className="tabular-nums text-right">{children}</dd>
    </>
  );
}

/**
 * Lancer ou figer la grille de temps de la séance.
 *
 * # Pourquoi ce bouton existe
 *
 * Une séance naît figée côté service, et la mise en marche était entièrement
 * automatique : elle partait à la jointure, une fois, sans rien dire si elle
 * échouait. Une séance restée en pause n'offrait alors **aucune prise** — le
 * studio n'a pas de commande de transport, et basculer la session locale se
 * faisait corriger par la métadonnée suivante.
 *
 * C'est donc la porte de secours autant que le geste normal : ce qu'il envoie
 * va au service, seul arbitre de la position partagée, et revient à tout le
 * monde par la métadonnée de salle.
 *
 * `paused` à `null` veut dire qu'aucun état n'a encore été lu : il n'y a alors
 * rien à afficher de vrai, et le bouton se retire plutôt que de mentir sur le
 * sens de son clic.
 */
function TransportControl({
  paused,
  noun,
  failure,
  onChange,
}: {
  paused: boolean | null;
  /** « le récital », pour qui le donne ; « le live » ailleurs. */
  noun: string;
  failure: string | null;
  onChange: (paused: boolean) => Promise<boolean>;
}) {
  const [pending, setPending] = useState(false);

  if (paused === null) {
    return null;
  }

  const handleClick = async () => {
    setPending(true);

    try {
      await onChange(!paused);
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        disabled={pending}
        onClick={() => void handleClick()}
        className="w-full h-9 rounded-lg text-[12px] font-medium text-text-primary bg-bg-elevated hover:bg-bg-hover transition-colors duration-150 cursor-pointer disabled:cursor-wait disabled:opacity-60"
      >
        {paused ? `Lancer ${noun}` : `Mettre ${noun} en pause`}
      </button>

      {failure !== null && <p className="px-1 text-[11px] text-red-300">{failure}</p>}
    </div>
  );
}

/**
 * « Mode Jam » : tout le monde joue, et la séance attend pour tomber ensemble.
 *
 * # Ce que l'interrupteur décide
 *
 * Le régime, pour **toute la séance** — pas pour ce poste. Allumé, tout le
 * monde émet, et le service publie pour la salle un même retard — le temps
 * qu'il faut à la note la plus lointaine pour arriver, avec une marge. C'est
 * ce qui fait que chacun entend tout au même instant. Sans ça,
 * on entend ses doigts tout de suite et son partenaire en différé : on le
 * croit en retard, on ralentit pour l'attendre, il fait de même, et le tempo
 * s'effondre. C'est le défaut le plus connu du jeu en réseau, et il gêne
 * davantage que la latence elle-même.
 *
 * L'allumer **retire le lead** : il n'y a plus de soliste, tout le monde
 * joue. L'éteindre laisse la salle sans lead jusqu'à ce que quelqu'un le
 * prenne — le premier qui annonce, ou celui qui clique.
 *
 * Il a été un réglage personnel — « attendre les autres », pour ce transport
 * seulement. C'était l'asymétrie exacte que le mode est censé corriger, chez
 * celui qui n'attendait pas. Il se bascule maintenant au service, comme la
 * pause, et reflète ce que le service publie : il bascule aussi quand c'est
 * quelqu'un d'autre qui l'a activé.
 *
 * # Ce qu'il n'applique pas
 *
 * Rien, localement : l'état revient par la métadonnée, quelques dizaines de
 * millisecondes plus tard. Le temps de la réponse, il se grise ; si le service
 * refuse, il le dit dessous et reste où la métadonnée le laisse.
 */
function JamSwitch({
  on,
  delayMs,
  onChange,
}: {
  on: boolean;
  /** Le délai que ce poste applique — en jam, le même que tout le monde. */
  delayMs: number;
  onChange: (on: boolean) => Promise<boolean>;
}) {
  const [pending, setPending] = useState(false);
  const [refused, setRefused] = useState(false);

  const handleClick = async () => {
    setPending(true);
    setRefused(false);

    try {
      const accepted = await onChange(!on);
      setRefused(!accepted);
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        role="switch"
        aria-checked={on}
        disabled={pending}
        onClick={() => void handleClick()}
        title={
          on
            ? "Tout le monde joue et attend le même délai : chacun entend tout au même instant."
            : "Une personne mène et s'entend à l'instant ; les autres écoutent, chacun avec son délai."
        }
        className={`flex w-full items-center justify-between gap-2 rounded-lg border px-2.5 py-2 text-[12px] transition-colors duration-150 cursor-pointer disabled:cursor-wait disabled:opacity-60 ${
          on
            ? "border-amber-500/30 bg-amber-500/[0.07] hover:bg-amber-500/10"
            : "border-border-subtle bg-white/[0.02] hover:bg-white/[0.04]"
        }`}
      >
        <span className="flex items-baseline gap-1.5">
          <span className={on ? "text-text-primary font-medium" : "text-text-secondary"}>
            Mode Jam
          </span>

          {on && delayMs > 0 && (
            <span className={`tabular-nums ${verdictColor(delayMs)}`}>
              +{Math.round(delayMs)} ms
            </span>
          )}
        </span>

        <span
          aria-hidden
          className={`relative h-4 w-7 shrink-0 rounded-full transition-colors duration-150 ${
            on ? "bg-amber-500/60" : "bg-white/15"
          }`}
        >
          <span
            className={`absolute top-0.5 size-3 rounded-full bg-white transition-all duration-150 ${
              on ? "left-3.5" : "left-0.5"
            }`}
          />
        </span>
      </button>

      <p className="px-1 text-[11px] text-text-muted">{jamHint(on, delayMs)}</p>

      {refused && (
        <p className="px-1 text-[11px] text-red-300">
          {"Le service n'a pas pris le changement. Le motif est dans la console du navigateur."}
        </p>
      )}
    </div>
  );
}

/**
 * Ce que le mode fait, en une phrase et sans jargon — et pour qui : la ligne
 * dit que c'est toute la séance qui bascule, pas ce poste, et que l'allumer
 * retire le lead.
 */
function jamHint(on: boolean, delayMs: number): string {
  if (!on) {
    return "Pour toute la salle : retire le lead, tout le monde joue, et tout le monde attend le même délai pour tomber ensemble.";
  }

  const delay = Math.round(delayMs);

  if (delay <= 0) {
    return "Tout le monde joue, et attendra le même délai dès que les liaisons seront mesurées.";
  }

  return `Tout le monde joue et attend ${delay} ms : tout le monde tombe ensemble.`;
}

export function verdictColor(outboundMs: number): string {
  if (outboundMs <= 25) {
    return "text-emerald-300";
  }

  if (outboundMs <= 40) {
    return "text-amber-300";
  }

  return "text-red-300";
}
